/**
 * Free Story Tracker
 *
 * Tracks how many free stories a non-signed-in user has accessed.
 * Uses a browser fingerprint (same in regular and incognito mode) stored
 * as a Firestore document ID, with localStorage as a fast-read cache.
 * sessionStorage acts as a within-session fallback when both Firestore
 * and localStorage are unavailable (e.g. Firestore down in incognito).
 *
 * Free story limit: FREE_STORY_LIMIT (3)
 */

import { getFirestore, doc, getDoc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';

export const FREE_STORY_LIMIT = 3;

// ─── Fingerprint ─────────────────────────────────────────────────────────────
// djb2 hash — fast, deterministic, no crypto needed
function djb2(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
    hash |= 0; // keep as 32-bit int
  }
  return Math.abs(hash).toString(36);
}

function buildFingerprint() {
  const parts = [
    navigator.userAgent || '',
    (Intl.DateTimeFormat().resolvedOptions().timeZone) || '',
    `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
    navigator.language || '',
    String(navigator.hardwareConcurrency || 0),
    String(new Date().getTimezoneOffset()),
    navigator.platform || '',
    String(navigator.maxTouchPoints || 0),
    String(navigator.deviceMemory || 0),
  ].join('||');
  return 'fp_' + djb2(parts);
}

// Get or create the fingerprint — localStorage is the cache; generating it
// from browser properties ensures the same value even in incognito.
function getFingerprint() {
  const LS_KEY = 'dlingo_fp';
  try {
    const cached = localStorage.getItem(LS_KEY);
    if (cached && cached.startsWith('fp_')) return cached;
  } catch {}
  const fp = buildFingerprint();
  try { localStorage.setItem(LS_KEY, fp); } catch {}
  return fp;
}

// ─── localStorage helpers (synchronous cache) ─────────────────────────────────
const LS_STORIES_KEY = 'dlingo_free_stories';

function getLocalStories() {
  try {
    const raw = localStorage.getItem(LS_STORIES_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

// ─── sessionStorage helpers — within-session fallback ─────────────────────────
// Incognito clears localStorage between sessions but sessionStorage persists
// within the same tab session, limiting per-session bypass to FREE_STORY_LIMIT.
const SS_STORIES_KEY = 'dlingo_ss_stories';

function getSessionStories() {
  try {
    const raw = sessionStorage.getItem(SS_STORIES_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function saveSessionStories(arr) {
  try { sessionStorage.setItem(SS_STORIES_KEY, JSON.stringify(arr)); } catch {}
}

function saveLocalStories(arr) {
  try { localStorage.setItem(LS_STORIES_KEY, JSON.stringify(arr)); } catch {}
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Synchronous (localStorage + sessionStorage) count of free stories used.
 * Use for immediate UI rendering before the Firestore fetch completes.
 */
export function getFreeStoriesUsedSync() {
  const local = getLocalStories();
  const session = getSessionStories();
  // Merge both sets so whichever has more data wins
  const merged = [...new Set([...local, ...session])];
  return merged.length;
}

/**
 * Async: fetch authoritative data from Firestore.
 * Falls back to localStorage if Firestore is unavailable.
 * Returns { stories: string[], count: number }
 */
export async function getFreeStoriesData() {
  const fp = getFingerprint();
  try {
    const db = getFirestore();
    const ref = doc(db, 'free_previews', fp);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      const stories = Array.isArray(data.stories) ? data.stories : [];
      // Sync both caches with authoritative server data
      saveLocalStories(stories);
      saveSessionStories(stories);
      return { stories, count: stories.length };
    }
    // Doc doesn't exist yet — merge local and session caches
    const local = getLocalStories();
    const session = getSessionStories();
    const merged = [...new Set([...local, ...session])];
    return { stories: merged, count: merged.length };
  } catch {
    // Firestore unavailable — use the more conservative of local vs session
    const local = getLocalStories();
    const session = getSessionStories();
    const merged = [...new Set([...local, ...session])];
    return { stories: merged, count: merged.length };
  }
}

/**
 * Record that a story was accessed.
 * Idempotent — calling multiple times with the same id is safe.
 */
export async function recordFreeStoryAccess(storyId) {
  if (!storyId) return;

  // Update both caches immediately (optimistic)
  const local = getLocalStories();
  if (!local.includes(storyId)) { local.push(storyId); saveLocalStories(local); }
  const session = getSessionStories();
  if (!session.includes(storyId)) { session.push(storyId); saveSessionStories(session); }

  // Persist to Firestore
  const fp = getFingerprint();
  try {
    const db = getFirestore();
    const ref = doc(db, 'free_previews', fp);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        stories: [storyId],
        fingerprint: fp,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    } else {
      const existing = snap.data().stories || [];
      if (!existing.includes(storyId)) {
        await updateDoc(ref, {
          stories: arrayUnion(storyId),
          updatedAt: new Date().toISOString(),
        });
      }
    }
  } catch (e) {
    // Non-fatal — localStorage already updated
    console.warn('[FreeStoryTracker] Firestore write failed (non-blocking):', e.message);
  }
}
